/** @jsx CanvaSX */
import { CanvaSX } from '@lib/canvasx/canvasx';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import {
  SPACER_2,
  SPACER_3,
  SPACER_4,
  SPACER_6,
  SPACER_9,
} from '../utils/style';
import { FlexColumn } from '@lib/canvasx/components/FlexColumn';
import { svgToURL } from '../utils/svg';
import { Rect } from '@lib/canvasx/components/Rect';
import { Image } from '@lib/canvasx/components/Image';
import phoneVerticalNotch from '../assets/icons/phoneVerticalNotch.svg';
import phoneHorizontalNotch from '../assets/icons/phoneHorizontalNotch.svg';
import phoneStatusBarIcons from '../assets/icons/phoneStatusBarIcons.svg';
import tabletStatusBarIcons from '../assets/icons/tabletStatusBarIcons.svg';
import phoneStatusBarTime from '../assets/icons/phoneStatusBarTime.svg';
import tabletCamera from '../assets/icons/tabletCamera.svg';

const PADDING_VERTICAL_PHONE = SPACER_4;
const PADDING_VERTICAL_TABLET = SPACER_3 + 2;
const PADDING_HORIZONTAL = SPACER_6;
const CAMERA_SIZE = SPACER_3;
// Use min height to avoid notch shift when status bar is toggled
const MIN_STATUS_BAR_HEIGHT = SPACER_6;
// Push the notch to the left when in landscape mode
const HORIZONTAL_NOTCH_PADDING = SPACER_9;

export interface MobileStatusBarProps {
  width: number;
  height: number;
  device: 'phone' | 'tablet';
  orientation: 'portrait' | 'landscape';
  notch: boolean;
  statusBar: boolean;
}

export const MobileStatusBar: CanvaSXFC<MobileStatusBarProps> = function (
  props
) {
  const { width, height, device, notch, orientation, statusBar } = props;
  const isPortrait = orientation === 'portrait';
  const isLandscape = !isPortrait;
  const isPhone = device === 'phone';
  const paddingVertical = isPhone
    ? PADDING_VERTICAL_PHONE
    : PADDING_VERTICAL_TABLET;
  // Ensure notch is centered vertically in landscape mode.
  const HORIZONTAL_STATUS_BAR_HEIGHT = MIN_STATUS_BAR_HEIGHT + paddingVertical;
  const notchPlaceholder = <Rect flexGrow />;
  // Subtract an additional 1 from the width to avoid floating point math
  // that sometimes pushes stauts bar items onto second row. Using Math.round is
  // is not ideal as it creates a jitter affect when resizing
  const statusBarItemWidth = (width - 1 - 2 * PADDING_HORIZONTAL) / 3;

  // Helper function to get the notch component based on the device and orientation
  const getNotchComponent = () => {
    if (!notch) return <Rect flexGrow />;
    if (isPhone) {
      return (
        <Image
          src={svgToURL(isPortrait ? phoneVerticalNotch : phoneHorizontalNotch)}
          width={isPortrait ? 81 : 25}
          height={isPortrait ? 24 : 82}
        />
      );
    } else {
      return (
        <Image
          src={svgToURL(tabletCamera)}
          width={CAMERA_SIZE}
          height={CAMERA_SIZE}
          {...(isPortrait
            ? { y: -CAMERA_SIZE - paddingVertical }
            : { x: CAMERA_SIZE / 2 + PADDING_HORIZONTAL / 2 })}
        />
      );
    }
  };

  const getStatusBarTime = () => {
    const width = 36;
    const height = 17;
    if (!statusBar) return <Rect width={width} height={height} />;
    const paddingLeft = isPhone ? SPACER_3 : SPACER_2;
    return (
      <Image
        src={svgToURL(phoneStatusBarTime)}
        width={width}
        height={height}
        padding={{ left: paddingLeft }}
      />
    );
  };

  // Helper function to get the status bar icons based on the device
  const getStatusBarIcons = () => {
    const width = isPhone ? 69 : 103;
    const height = isPhone ? 12 : 24;
    if (!statusBar) return <Rect width={width} height={height} />;
    return (
      <Image
        src={svgToURL(isPhone ? phoneStatusBarIcons : tabletStatusBarIcons)}
        width={width}
        height={height}
      />
    );
  };

  return (
    <FlexRow
      width={width}
      verticalAlignment='center'
      minHeight={
        isPortrait
          ? MIN_STATUS_BAR_HEIGHT
          : height - HORIZONTAL_STATUS_BAR_HEIGHT
      }
      padding={{
        left: PADDING_HORIZONTAL,
        right: PADDING_HORIZONTAL,
        top: paddingVertical,
      }}
    >
      <FlexColumn width={statusBarItemWidth} horizontalAlignment='left'>
        {getStatusBarTime()}
      </FlexColumn>
      {isPortrait && (
        <FlexColumn width={statusBarItemWidth} horizontalAlignment='center'>
          {getNotchComponent()}
        </FlexColumn>
      )}
      {isLandscape && notchPlaceholder}
      <FlexColumn width={statusBarItemWidth} horizontalAlignment='right'>
        {getStatusBarIcons()}
      </FlexColumn>
      {isLandscape && (
        <FlexColumn
          width={width}
          padding={{ right: HORIZONTAL_NOTCH_PADDING }}
          flexGrow
          horizontalAlignment='right'
        >
          <FlexRow verticalAlignment='center'>{getNotchComponent()}</FlexRow>
        </FlexColumn>
      )}
    </FlexRow>
  );
};
